/**
 * PdfPreviewDialog — in-page PDF viewer.
 *
 * Two rendering paths:
 *
 *  1. **Native iframe** (iOS Safari + desktop browsers) — blob URL fed to
 *     an `<iframe>`. Fast, uses the OS renderer, handles every PDF quirk.
 *     iOS Safari ignores `#zoom=` fragments, so zoom is applied via
 *     `transform: scale()` on the iframe itself.
 *
 *  2. **Canvas renderer** (Android — Samsung Internet / Chrome) — Android
 *     browsers refuse to render PDFs inline in iframes and drop back to a
 *     download-only "Open" placeholder (see user bug report 3 May 2026).
 *     For those we use `react-pdf` to paint each page into a canvas, which
 *     works identically across every Android browser version.
 *
 * Zoom choice is persisted to localStorage so it survives tab close,
 * browser restart, and navigating between documents — the user's
 * preferred zoom sticks across sessions on the same device.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Download, Minus, Plus, Loader2 } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { triggerDownload } from '../services/api';

// pdfjs worker needs to be served same-origin (avoid CSP + third-party
// firewalls). The Lutrom deploy ships the worker at /pdf.worker.min.mjs
// (copied from `pdfjs-dist/build/pdf.worker.min.mjs` into
// `frontend/public/`). Some production static hosts (including the
// current Lutrom deploy target) serve `.mjs` with the wrong MIME type
// (`application/octet-stream`), which browsers refuse to load as a JS
// module — pdfjs then throws "Setting up fake worker failed:
// 'application/octet-stream' is not a valid JavaScript MIME type".
//
// Fix: fetch the worker ourselves, rewrap it in a Blob with an explicit
// `application/javascript` MIME, and point pdfjs at the resulting blob:
// URL. Browsers honour the blob's declared type as the module MIME, so
// the "fake worker" fallback path is bypassed and the real Web Worker
// spins up regardless of what Content-Type the static host emits.
// Same-origin fetch means no CSP / cross-origin worker errors and no
// third-party network dependency — the worker still lives in our
// bundle. On the happy path (preview / correct MIME), this incurs one
// tiny extra fetch of the already-downloaded worker file; browsers
// serve the second request from HTTP cache.
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
const WORKER_READY = (async () => {
  try {
    const resp = await fetch('/pdf.worker.min.mjs');
    if (!resp.ok) throw new Error(`worker fetch ${resp.status}`);
    const text = await resp.text();
    const blob = new Blob([text], { type: 'application/javascript' });
    pdfjs.GlobalWorkerOptions.workerSrc = URL.createObjectURL(blob);
  } catch {
    // Fall back to the same-origin path; if MIME is correct this still works.
    pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
  }
})();

const ZOOM_KEY = 'lutrom.pdfPreview.zoom';
const FIT = 'fit';

const readInitialZoom = () => {
  try {
    const v = localStorage.getItem(ZOOM_KEY);
    if (v === FIT) return FIT;
    const n = parseInt(v, 10);
    if (Number.isFinite(n) && n >= 50 && n <= 200) return n;
  } catch { /* ignore */ }
  return FIT;
};

export const PdfPreviewDialog = ({ url, filename, onClose, title = 'PDF preview' }) => {
  const [zoom, setZoom] = useState(readInitialZoom);

  useEffect(() => {
    try { localStorage.setItem(ZOOM_KEY, String(zoom)); } catch { /* ignore */ }
  }, [zoom]);

  // Hint the built-in viewer on the default zoom — works on desktop,
  // ignored on iOS (handled by CSS transform below).
  const src = useMemo(() => {
    if (!url) return '';
    return `${url}#toolbar=0&view=Fit&zoom=page-fit`;
  }, [url]);

  const sliderValue = zoom === FIT ? 100 : zoom;
  const label = zoom === FIT ? 'Fit' : `${zoom}%`;

  // We render every PDF via react-pdf's canvas pipeline. The previous
  // iframe-based path was great on desktop Chrome/Firefox but broke
  // in subtle ways on every other surface (Android stock browser
  // showed a download placeholder, iOS Safari only rendered page 1,
  // Samsung Internet failed on cross-context blob fetches). One code
  // path means one set of bugs, identical zoom/scroll/pinch UX
  // across phones, tablets, and desktops.
  const scale = zoom === FIT ? 1 : zoom / 100;

  return (
    <Dialog open={!!url} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent
        className="max-w-3xl w-[calc(100vw-1rem)] sm:w-full p-0 overflow-hidden flex flex-col max-h-[94vh]"
        data-testid="pdf-preview-dialog"
      >
        {/* Header — title only. The Shadcn DialogContent owns the absolute X
            close button at top-right; we keep this row clear of any other
            buttons so they can't physically overlap. */}
        <DialogHeader className="px-3 py-2.5 pr-12 border-b border-slate-200 space-y-0">
          <DialogTitle className="text-sm truncate pr-2">{filename || title}</DialogTitle>
          <DialogDescription className="text-xs hidden sm:block">
            In-page preview. Use the slider to zoom.
          </DialogDescription>
        </DialogHeader>

        {/* Zoom + download row — always visible, compact on mobile. */}
        <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-slate-200 bg-slate-50">
          <Button
            size="icon" variant="ghost" className="h-7 w-7 shrink-0"
            onClick={() => setZoom(Math.max(50, (zoom === FIT ? 100 : zoom) - 10))}
            data-testid="pdf-zoom-out"
            title="Zoom out"
          >
            <Minus className="h-3.5 w-3.5" />
          </Button>
          <div className="flex-1 min-w-0">
            <Slider
              value={[sliderValue]}
              min={50} max={200} step={10}
              onValueChange={(v) => setZoom(v[0])}
              data-testid="pdf-zoom-slider"
            />
          </div>
          <Button
            size="icon" variant="ghost" className="h-7 w-7 shrink-0"
            onClick={() => setZoom(Math.min(200, (zoom === FIT ? 100 : zoom) + 10))}
            data-testid="pdf-zoom-in"
            title="Zoom in"
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
          <span className="text-xs tabular-nums text-slate-600 w-9 text-right shrink-0" data-testid="pdf-zoom-label">
            {label}
          </span>
          <Button
            size="sm" variant="ghost" className="h-7 px-2 text-xs shrink-0"
            onClick={() => setZoom(FIT)}
            data-testid="pdf-zoom-fit"
            title="Fit whole page"
          >
            Fit
          </Button>
          <div className="w-px h-5 bg-slate-300 mx-1 shrink-0" aria-hidden />
          <Button
            size="icon" variant="ghost" className="h-7 w-7 shrink-0"
            onClick={() => { if (url) triggerDownload(url, filename); }}
            data-testid="pdf-preview-download-btn"
            title="Download PDF"
          >
            <Download className="h-3.5 w-3.5" />
          </Button>
        </div>

        <div
          className="bg-slate-100 w-full overflow-auto"
          style={{ height: 'calc(94vh - 100px)' }}
          data-testid="pdf-preview-scroll"
        >
          {url && <PdfCanvasViewer url={url} zoom={zoom} title={title} />}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Canvas-based PDF viewer used for every device (desktop + mobile).
 * Paints each page of the PDF into its own canvas via react-pdf and
 * scrolls the container to paginate. Zoom is multiplicative on the
 * react-pdf `scale` prop.
 */
const PdfCanvasViewer = ({ url, zoom, title }) => {
  const wrapRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [error, setError] = useState(null);
  // Wait for the workerSrc rewrap (see top of file) to finish before we
  // hand a PDF to react-pdf. If the user pops open a PDF before the
  // blob-URL swap completes, react-pdf would try to load the .mjs
  // directly and hit the octet-stream MIME error — this gate makes the
  // whole flow race-free.
  const [workerReady, setWorkerReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    WORKER_READY.then(() => { if (!cancelled) setWorkerReady(true); });
    return () => { cancelled = true; };
  }, []);
  // Prefetch the blob as an ArrayBuffer on the main thread. This is more
  // robust than letting pdf.js fetch a `blob:` URL from inside the Web
  // Worker — some Android browsers (notably Samsung Internet on the Tab
  // A8) silently fail that cross-context blob access, which is what
  // killed the first rev of this viewer.
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    setNumPages(0);
    (async () => {
      try {
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`fetch ${resp.status}`);
        const buf = await resp.arrayBuffer();
        if (!cancelled) setData(new Uint8Array(buf));
      } catch (e) {
        if (!cancelled) setError(e?.message || 'download failed');
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  // Measure the scroll container so we can render pages at fit-to-width
  // when the user asks for Fit. Re-measure on resize + orientation change
  // so rotating the Tab A8 doesn't leave pages at the old width.
  useEffect(() => {
    const measure = () => {
      if (wrapRef.current) setContainerWidth(wrapRef.current.clientWidth);
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, []);

  // react-pdf keys the Document by referential identity of `file`. Keep
  // the same object reference while `data` is unchanged so the renderer
  // doesn't throw away work on every parent re-render (e.g. zoom tick).
  const fileProp = useMemo(() => (data ? { data } : null), [data]);

  // Re-enable the Web Worker now that we pull the worker script from
  // unpkg with proper CORS/MIME — main-thread parsing blocked the UI on
  // the Tab A8 for larger PDFs. If the worker fetch ever fails (strict
  // corporate proxies, CSP blocking unpkg) we still surface the pdf.js
  // error on-screen for diagnosis.
  const docOptions = useMemo(() => ({}), []);

  if (error) {
    return (
      <div className="p-6 text-center text-sm text-slate-600 space-y-2" data-testid="pdf-android-error">
        <div>Couldn&apos;t render this PDF inline. Tap the download button above to open it in your PDF viewer.</div>
        <div className="text-xs text-slate-400 font-mono break-all">{error}</div>
      </div>
    );
  }

  return (
    <div
      ref={wrapRef}
      className="w-full flex flex-col items-center gap-2 py-2"
      data-testid="pdf-android-canvas"
    >
      {!fileProp || !workerReady ? (
        <div className="flex items-center gap-2 py-10 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading PDF…
        </div>
      ) : (
        <Document
          file={fileProp}
          options={docOptions}
          onLoadSuccess={({ numPages: n }) => setNumPages(n)}
          onLoadError={(e) => setError(e?.message || 'pdf load failed')}
          onSourceError={(e) => setError(`source: ${e?.message || 'unknown'}`)}
          loading={
            <div className="flex items-center gap-2 py-10 text-slate-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Rendering…
            </div>
          }
        >
          {Array.from({ length: numPages }, (_, i) => {
            const base = containerWidth ? containerWidth - 16 : 0;
            const pageProps = zoom === FIT
              ? (base ? { width: base } : { scale: 1 })
              : (base ? { width: base * (zoom / 100) } : { scale: zoom / 100 });
            return (
              <Page
                key={`p-${i}`}
                pageNumber={i + 1}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                className="bg-white shadow-sm mb-1"
                {...pageProps}
              />
            );
          })}
        </Document>
      )}
      {numPages > 0 && (
        <div className="text-[11px] text-slate-400 py-1" data-testid="pdf-android-pagecount">
          {title} — {numPages} page{numPages === 1 ? '' : 's'}
        </div>
      )}
    </div>
  );
};

export default PdfPreviewDialog;
